import { orgUiPath } from '@/core/api';
import { CAMPAIGN_QUERY, parseCampaignIdFromSearch } from '@/core/urlRouting';
import type { Creative } from '@/types/api';

export type CreativeEditorFrom = 'creatives' | 'campaign';

export interface CreativeEditorLocationState {
  from?: CreativeEditorFrom;
  campaignId?: number;
}

export interface CreativeEditorBackTarget {
  path: string;
  label: string;
}

/** Shareable Neo editor href (prefer over router-only ``state``). */
export function creativeEditorHref(
  orgId: string | number,
  creativeId: number,
  from: CreativeEditorFrom,
  campaignId?: number | null,
): string {
  const base = orgUiPath(orgId, `/creatives/${creativeId}`);
  if (from === 'campaign' && campaignId != null && !Number.isNaN(campaignId)) {
    return `${base}?${CAMPAIGN_QUERY}=${campaignId}`;
  }
  return base;
}

/** Router `state` when opening the creative editor from a list or campaign. */
export function creativeEditorLinkState(
  from: CreativeEditorFrom,
  campaignId?: number | null,
): CreativeEditorLocationState {
  if (from === 'campaign' && campaignId != null && !Number.isNaN(campaignId)) {
    return { from: 'campaign', campaignId };
  }
  return { from: 'creatives' };
}

/** Resolve Neo/legacy editor back navigation from router state with creative fallback. */
export function resolveCreativeEditorBackTarget(
  orgId: string,
  state: CreativeEditorLocationState | null | undefined,
  creative?: Pick<Creative, 'assigned_campaign_ids'> | null,
  search?: string,
): CreativeEditorBackTarget {
  const campaignFromQuery = search != null ? parseCampaignIdFromSearch(search) : null;
  if (campaignFromQuery != null) {
    return {
      path: orgUiPath(orgId, `/campaigns/${campaignFromQuery}`),
      label: 'Campaign',
    };
  }

  if (state?.from === 'campaign' && state.campaignId != null) {
    return {
      path: orgUiPath(orgId, `/campaigns/${state.campaignId}`),
      label: 'Campaign',
    };
  }
  if (state?.from === 'creatives') {
    return {
      path: orgUiPath(orgId, '/creatives'),
      label: 'Creatives',
    };
  }

  const fallbackCampaignId = creative?.assigned_campaign_ids?.[0];
  if (fallbackCampaignId != null) {
    return {
      path: orgUiPath(orgId, `/campaigns/${fallbackCampaignId}`),
      label: 'Campaign',
    };
  }
  return {
    path: orgUiPath(orgId, '/creatives'),
    label: 'Creatives',
  };
}
